import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../../api/axiosInstance";
import { AxiosError } from "axios";
import { ArithmeticalEvaluation, ArithmeticalEvaluationByYear, EvaluatedRight, IndicatorTypeEvaluation } from "../../../interfaces/Evaluations";
import { RootState } from "../../store";
const { REACT_APP_API_PATH } = process.env;

interface GetEvaluationQuery {
    right: string;
    period?: string;
}

interface GetArithmeticalEvaluationQuery {
    right: string
    period?: string;
}

interface GetEvaluationResponse {
    evaluation_right: EvaluatedRight | null
    evaluations_indicator_type: IndicatorTypeEvaluation[] | null,
}

interface GetArithmeticalEvaluationResponse {
    arithmetical_evaluation_right: ArithmeticalEvaluation | null,
}

interface ErrorResponse {
    message: string;
    statusCode: number;
}

export const getEvaluationRight = createAsyncThunk<
    GetEvaluationResponse,
    GetEvaluationQuery,
    { rejectValue: ErrorResponse; state: RootState }
>(
    "evaluation/evaluation_right",
    async ({ right, period }: GetEvaluationQuery, thunkAPI) => {
        const { rejectWithValue } = thunkAPI;
        // Declarar variables inicializadas como null
        let evaluation_right;
        let evaluations_indicator_type;

        let url1 = `${REACT_APP_API_PATH}/evaluacion/trivalente?derecho=${right}`;
        let url2 = `${REACT_APP_API_PATH}/estructurales?derechoId=${right}`;

        if (period) {
            url1 += `&periodo=${period}`;
            url2 += `&periodo=${period}`;
        }

        try {

            // Realiza ambas llamadas en paralelo con Promise.allSettled
            const results = await Promise.allSettled([
                axiosInstance.get(url1),
                axiosInstance.get(url2)
            ]);

            // Procesar el resultado de cada promesa
            const [result1, result2] = results;
            const { periods } = thunkAPI.getState() as RootState;

            const evaluatedPeriod = periods.periods?.find((p) => {
                const match = p.value.match(/\d{4}$/);
                return match && match[0] === String(period);
            });

            if (result1.status === "fulfilled") {
                const rawDerecho = result1.value.data.derecho;
                if (evaluatedPeriod?.estado === 2 && period) {
                    evaluation_right = {
                        ...rawDerecho,
                        trivalente_derecho: 2   // <- status for "En proceso"
                    };
                } else {
                    evaluation_right = rawDerecho;
                }
            }

            if (result1.status === "fulfilled" && result2.status === "fulfilled") {
                const data2 = result2.value.data;

                const allTiposIndicadores: IndicatorTypeEvaluation[] = [
                    ...(evaluation_right?.tipos_indicadores || []).filter((type: any) => type.nombre !== "Estructurales"),
                    ...data2.derecho.tipos_indicadores
                ];

                if (evaluatedPeriod?.estado === 2 && period) {
                    evaluations_indicator_type = allTiposIndicadores.map((tipo) => ({
                        ...tipo,
                        trivalente_tipo_indicador: 2,  // <- status for "En proceso"
                        dimensiones: tipo.dimensiones?.map((dimension) => ({
                            ...dimension,
                            trivalente_dimension: 2,  // <- status for "En proceso"
                            subdimensiones: dimension.subdimensiones?.map((sub) => ({
                                ...sub,
                                trivalente_subdimension: 2,  // <- status for "En proceso"
                            })) ?? []
                        })) ?? []
                    }));
                } else {
                    evaluations_indicator_type = allTiposIndicadores;
                }
            } else {
                const nonEstructurales: IndicatorTypeEvaluation[] = [
                    ...(evaluation_right?.tipos_indicadores || []).filter((type: any) => type.nombre !== "Estructurales"),
                ];

                if (evaluatedPeriod?.estado === 2 && period) {
                    evaluations_indicator_type = nonEstructurales.map((tipo) => ({
                        ...tipo,
                        trivalente_tipo_indicador: 2,
                        dimensiones: tipo.dimensiones?.map((dimension) => ({
                            ...dimension,
                            trivalente_dimension: 2,
                            subdimensiones: dimension.subdimensiones?.map((sub) => ({
                                ...sub,
                                trivalente_subdimension: 2,
                            })) ?? []
                        })) ?? []
                    }));
                } else {
                    evaluations_indicator_type = nonEstructurales;
                }
            }

            if (!evaluation_right) {
                throw new Error("No se pudo obtener la evaluación del derecho.");
            }

        } catch (error) {
            if (error instanceof AxiosError && error.response) {
                return rejectWithValue({
                    message: error.response.data?.message || 'Error desconocido',
                    statusCode: error.response.status,
                });
            }
            return rejectWithValue({
                message: 'Error de red o servidor',
                statusCode: 500,
            });
        }

        return {
            evaluation_right,
            evaluations_indicator_type,
            error: false
        };
    }
);

export const getArithmeticalEvaluation = createAsyncThunk<
    GetArithmeticalEvaluationResponse,
    GetArithmeticalEvaluationQuery,
    { rejectValue: ErrorResponse }
>(
    "evaluation/arithmetical_evaluation_right",
    async ({ right, period }: GetArithmeticalEvaluationQuery, thunkAPI) => {
        const { rejectWithValue } = thunkAPI;

        try {
            let url = `${REACT_APP_API_PATH}/evaluacion/sumatoria?derecho=${right}`;

            const response = await axiosInstance.get(url);
            const data: ArithmeticalEvaluationByYear = response.data;

            if (!data || Object.keys(data).length === 0) {
                return rejectWithValue({
                    message: 'No hay datos disponibles para el periodo solicitado.',
                    statusCode: 404,
                });
            }

            const { periods } = thunkAPI.getState() as RootState;

            const evaluatedPeriod = periods.periods?.find((p) => {
                const match = p.value.match(/\d{4}$/);
                return match && match[0] === String(period);
            });

            const yearKey = String(period);

            // Caso 1: No existe ese año en data
            if (!(yearKey in data)) {
                if (evaluatedPeriod?.estado === 2) {
                    return {
                        arithmetical_evaluation_right: {
                            total: 0,
                            positivos: 0,
                            negativos: 0,
                            neutros: 0,
                            total_con_valor: -1,
                        },
                        error: false,
                    };
                } else {
                    return {
                        arithmetical_evaluation_right: null,
                        error: false,
                    };
                }
            }

            // Caso 2: Año está en data, y además el estado es 2 → devolver "en proceso"
            if (evaluatedPeriod?.estado === 2) {
                return {
                    arithmetical_evaluation_right: {
                        total: 0,
                        positivos: 0,
                        negativos: 0,
                        neutros: 0,
                        total_con_valor: -1,
                    },
                    error: false,
                };
            }

            // Caso 3: Año está en data y no está en proceso → devolver datos reales
            return {
                arithmetical_evaluation_right: data[yearKey],
                error: false,
            };

        } catch (error) {
            if (error instanceof AxiosError && error.response) {
                return rejectWithValue({
                    message: error.response.data?.message || 'Error desconocido',
                    statusCode: error.response.status,
                });
            }
            return rejectWithValue({
                message: 'Error de red o servidor',
                statusCode: 500,
            });
        }
    }
);

